import type { PocInputValidity } from 'commons/types';
import { ConfirmationModalComponent } from 'commons/components';
import {
    deleteFilterFromUserPreferences,
    updateFilterFromUserPreferences,
} from 'core/service/user-preferences/user-preferences.service';
import { useLocations } from 'core/custom-hooks/use-locations.hook';
import { PopupExtendedContext } from 'core/contexts/popup-extended.context';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n';
import { handleErrorPopupContext } from 'core/utils/handle-errors-context';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { UserCustomFilter } from 'core/utils/types/user-custom-filter.type';

import { SaveFilterModalComponent } from 'modules/global/pods/user-custom-filters/save-filter-modal.component';
import { mapOperatorListFiltersFromVmtoApi } from 'modules/operators/pods/private-pods/operators-list/operators-filter/domain/mapper/operators-list-filter.mapper';
import { OperatorsFilterFields } from 'modules/operators/types/operators-filter.type';
import { useContext } from 'react';
import { usePopupManager } from 'react-popup-manager';

export const useMenuFiltersHandler = () => {
    const { refreshUserPreferences, userPreferencesId = '' } = useUserPreferencesContext();

    const { locations } = useLocations();

    const { t } = useTranslation();

    const popupManager = usePopupManager();
    const popupManagerContext = useContext(PopupExtendedContext);

    const showConfirmDeleteFilterModal = <T>(filter: UserCustomFilter<T>, selected: string, onFinally: () => void) => {
        popupManager.open(ConfirmationModalComponent, {
            title: t('app.sidemenu.options.generic.submenu.popover.delete.filter.modal.title'),
            buttonText: t('app.sidemenu.options.generic.submenu.popover.delete.filter.modal.button'),
            children: t('app.sidemenu.options.generic.submenu.popover.delete.filter.modal.description', {
                filterName: filter.name,
            }),
            executeOnCloseOnAction: false,
            actionOnClick: () =>
                deleteFilterFromUserPreferences(filter.id!)
                    .then(() => {
                        showSuccessNotification(
                            t('app.sidemenu.options.generic.delete.filter.notification.positive', {
                                filterName: filter.name,
                            })
                        );
                        popupManager.closeAll();
                    })
                    .catch(() =>
                        showErrorNotification(
                            t('app.sidemenu.options.generic.delete.filter.notification.alert', {
                                filterName: filter.name,
                            })
                        )
                    )
                    .finally(() => {
                        // If deleted filter is selected, redirect to basic Entity view
                        if (filter.entity && `${filter.entity?.toLowerCase()}/filters/${filter.id}` === selected) {
                            onFinally();
                        }
                        popupManager.closeAll();
                        refreshUserPreferences();
                    }),
        });
    };

    const handleRenameFilter = async <T>(filter: UserCustomFilter<T>, filterName: string): Promise<boolean> => {
        try {
            await updateFilterFromUserPreferences(userPreferencesId, {
                filterId: filter.id,
                filterName,
                frontendData: {
                    filter: filter.fields,
                    sorting: filter.sorting,
                },
                queryData: JSON.stringify(
                    mapOperatorListFiltersFromVmtoApi(filter.fields as OperatorsFilterFields, locations)
                ),
            });
            showSuccessNotification(
                t('app.sidemenu.options.generic.rename.filter.notification.positive', { filterName })
            );
            refreshUserPreferences();
            return true;
        } catch (error) {
            try {
                const parsedError = JSON.parse((error as Error).message);
                if (parsedError.errors?.length > 0) {
                    handleErrorPopupContext(parsedError.errors, popupManagerContext);
                    return false;
                }
            } catch {
                // error message is not JSON — fall through to generic notification
            }
            showErrorNotification(
                t('app.sidemenu.options.generic.rename.filter.notification.alert', { filterName: filter.name })
            );
            return true;
        }
    };

    const showRenameFilterModal = <T>(filter: UserCustomFilter<T>) => {
        popupManager.open(SaveFilterModalComponent, {
            initialFilterName: filter.name,
            onCancel: () =>
                popupManagerContext.setData('RESET_DATA', { filterSaveValidity: {}, filterSaveErrors: undefined }),
            onValidity: (field: string, validity: PocInputValidity) => {
                popupManagerContext.setData('SET_DATA', {
                    ...popupManagerContext.data,
                    filterSaveValidity: {
                        ...popupManagerContext.data.filterSaveValidity,
                        [field]: validity,
                    },
                });
            },
            onSave: (filterName: string) => handleRenameFilter(filter, filterName),
        });
    };

    return {
        showConfirmDeleteFilterModal,
        showRenameFilterModal,
    };
};
